import { formatarIdentificacaoObra } from "@/shared/utils/formatar-identificacao-obra";
import {
  assignLanes,
  buildTimeline,
  monthEnd,
  monthStart,
  positionInterval,
} from "@/modules/carga-trabalho";
import type {
  CargaTrabalhoProfissional,
  GanttIndicators,
  IsoDate,
  Timeline,
} from "@/modules/carga-trabalho";
import type { EquipeObraTarget } from "@/modules/recursos";
import {
  buildProfissionalRows,
  conflictBands,
  pilulaDaObra,
  rangeLine,
} from "@/shared/components/gantt-carga/gantt-rows";
import type { GanttItem, GanttRowModel } from "@/shared/components/gantt-carga/gantt-rows";
import { NOVA_OBRA_LABEL } from "../equipe-obra.constants";
import { detectarConflitos, novaAlocacaoNaCarga } from "@/modules/recursos";
import type { IndisponibilidadesDoProfissional, NovaAlocacao } from "@/modules/recursos";

/** Meses de margem antes do início e depois do término da Nova Obra na janela do Gantt. */
export const CALENDARIO_MARGEM_MESES = 1;

/** Mês (`AAAA-MM`) de `date` deslocado em `meses`; negativo volta no tempo. */
function mesDeslocado(date: IsoDate, meses: number): string {
  const [ano = 1970, mes = 1] = date.split("-").map(Number);
  const alvo = new Date(Date.UTC(ano, mes - 1 + meses, 1));
  return `${alvo.getUTCFullYear()}-${String(alvo.getUTCMonth() + 1).padStart(2, "0")}`;
}

/**
 * Janela do Gantt: do mês anterior ao início ao mês seguinte ao término da Nova Obra (meses
 * inteiros, cobrindo também alocações novas fora do período). Alocações existentes que a
 * ultrapassam aparecem cortadas na borda, para as pílulas ficarem maiores.
 */
export function janelaDoCalendario(
  periodo: { inicio: IsoDate; fim: IsoDate },
  novas: NovaAlocacao[],
): { inicio: IsoDate; fim: IsoDate } {
  const inicios = [periodo.inicio, ...novas.map((nova) => nova.inicio)].sort();
  const fins = [periodo.fim, ...novas.map((nova) => nova.fim)].sort();
  const primeiro = inicios[0] ?? periodo.inicio;
  const ultimo = fins[fins.length - 1] ?? periodo.fim;
  const inicio = monthStart(mesDeslocado(primeiro, -CALENDARIO_MARGEM_MESES));
  const fim = monthEnd(mesDeslocado(ultimo, CALENDARIO_MARGEM_MESES));
  return { inicio, fim };
}

export interface CalendarioInput {
  obra: EquipeObraTarget;
  /** Membros da equipe na ordem da aba Papéis e Profissionais. */
  membros: { id: string; nome: string; time: string | null }[];
  novas: NovaAlocacao[];
  /** Alocações existentes no período (HU 1779). */
  carga: CargaTrabalhoProfissional[];
  indisponibilidades: IndisponibilidadesDoProfissional[];
  indicators: GanttIndicators;
  today: IsoDate;
}

/**
 * Gantt do Calendário de Alocação (HU 2059): uma linha por profissional da equipe, com
 * as alocações existentes (etapas, cancelada, indisponibilidade, conforme os indicadores)
 * e, nas faixas de cima, a Nova Obra tracejada — sempre visível. As faixas de conflito
 * mostram só a nova alocação contra outra obra, como informação (sem resolver).
 */
export function buildCalendarioRows(input: CalendarioInput): {
  rows: GanttRowModel[];
  timeline: Timeline;
} {
  const { obra, membros, novas, carga, indisponibilidades, indicators, today } = input;

  const profissionais: CargaTrabalhoProfissional[] = membros.map((membro) => {
    const existente = carga.find((item) => item.id === membro.id);
    return {
      id: membro.id,
      nome: existente?.nome || membro.nome,
      time: existente?.time ?? membro.time,
      alocacoes: existente?.alocacoes ?? [],
      indisponibilidades: indisponibilidades.find((item) => item.id === membro.id)?.periodos ?? [],
      conflitosIgnorados: [],
    };
  });

  const periodo = { inicio: obra.inicio, fim: obra.fim };
  const primeiraAlocacao = novas.map((nova) => nova.inicio).sort()[0];
  const timeline = buildTimeline(janelaDoCalendario(periodo, novas), primeiraAlocacao);

  const base = buildProfissionalRows({ periodo, profissionais }, timeline, indicators, today);

  const rows = base.map((row) => {
    const doMembro = novas.filter((nova) => nova.usuarioId === row.key);
    const novaLanes = assignLanes(doMembro);
    const novaLaneCount = doMembro.length > 0 ? Math.max(...novaLanes) + 1 : 0;

    const novaItems: GanttItem[] = doMembro.flatMap((nova, index) => {
      const position = positionInterval(nova, timeline);
      if (!position) return [];
      return [
        {
          key: `nova-${nova.id}`,
          kind: "bar" as const,
          lane: novaLanes[index] ?? 0,
          left: position.left,
          width: position.width,
          ...pilulaDaObra(obra),
          past: false,
          cancelada: false,
          transferida: true,
          segments: [{ kind: "transferida" as const, left: 0, width: 100 }],
          tooltip: [
            NOVA_OBRA_LABEL,
            `Obra ${formatarIdentificacaoObra(obra)}`,
            rangeLine("Alocação", nova),
          ].join("\n"),
        },
      ];
    });

    const conflitos = detectarConflitos(doMembro, obra, carga).map((conflito) => ({
      a: conflito.existente,
      b: novaAlocacaoNaCarga(conflito.nova, obra),
      inicio: conflito.inicio,
      fim: conflito.fim,
    }));
    const { bands, tooltip } = conflictBands(conflitos, timeline, `${row.key}-nova`);

    return {
      ...row,
      conflicts: conflitos.length,
      conflictTooltip: tooltip,
      conflitos: [],
      bands,
      items: [
        ...novaItems,
        ...row.items.map((item) => ({ ...item, lane: item.lane + novaLaneCount })),
      ],
      // Sem alocação existente, a linha tem só as faixas da Nova Obra.
      lanes: row.items.length > 0 ? row.lanes + novaLaneCount : Math.max(1, novaLaneCount),
    };
  });

  return { rows, timeline };
}
